Backbone = require 'backbone'
Util = require 'lib/util'
_ = require 'underscore'
log = require 'debug/log'

webSocketEvents = ['close', 'error', 'message', 'open']
DONT_COME_BACK_CODE = 4001

module.exports = class Socket
  @CONNECTING: 'connecting'
  @CONNECTED: 'connected'
  @WAITING_TO_RECONNECT: 'waiting to reconnect'
  @RECONNECTING: 'reconnecting'
  @CLOSED: 'closed'
  @DESTROYED: 'destroyed'
  @PING_TIMEOUT: 30000
  @WAKE_UP_CHECK_INTERVAL: 2 * 1000
  constructor: (@url,
      {
        @fxReconnectDelay
        @maxReconnectDelay
        @failedConsecutiveReconnectsLimit
        @reconnectRateLimit
        @reconnectRateWindow
        @maxReconnectsPerDay
      } = {}) ->
    _.extend(@, Backbone.Events)

    @ws = null

    @resetRetries()

    for event in webSocketEvents
      @listenTo @, event, @["on#{Util.capitalize(event)}"]

    if 'WebSocket' !of window
      @trigger('connect_failed')
      return

    @mustNeverReconnect = false

    @reconnectRateAttempts = 0
    @totalReconnects = 0

    @_setDailyFailsCheck()

    @connect()

    @buffer = []
    @connectedOnce = false

    @wakeUpCheckLastRun = Date.now()
    @_wakeUpCheck = setInterval =>
      currentTime = Date.now()
      # consider we just woke up if the last time we ran this interval, which
      # is supposed to run every two seconds, was more than [rate limit
      # window in ms] ago
      if currentTime > @wakeUpCheckLastRun + @reconnectRateWindow && !@mustNeverReconnect
        @disconnect()
        @reconnect()
      @wakeUpCheckLastRun = currentTime
    , Socket.WAKE_UP_CHECK_INTERVAL

    @trigger('connecting')

  _setDailyFailsCheck: ->
    @_dailyFailsCheck = setInterval =>
      @totalReconnects = 0
      if @state == Socket.CLOSED
        @reconnect()
    , Util.getMs(days: 1)

  _setReconnectBackOff: ->
    if @_timeoutBackOff?
      return

    @_timeoutBackOff = setTimeout =>
      clearTimeout(@_timeoutBackOff)
      delete @_timeoutBackOff

      @reconnectRateAttempts = 0

      if @state == Socket.CLOSED
        @reconnect()
    , @reconnectRateWindow

  resetRetries: ->
    @failedConsecutiveReconnectAttempts = 0

  _clearPingTimeout: ->
    if @_pingTimeout?
      clearTimeout(@_pingTimeout)
    @_pingTimeout = null

  _resetPingTimeout: ->
    @_clearPingTimeout()
    @_pingTimeout = setTimeout @_noPing.bind(@), Socket.PING_TIMEOUT

  _noPing: ->
    if !@state == Socket.CONNECTED
      return
    @disconnect()

  connect: ->
    if @ws?
      for event in webSocketEvents
        @ws["on#{event}"] = null

    @connectAttemptStarted = Date.now()
    @ws = new WebSocket(@url)

    for event in webSocketEvents
      do (event) =>
        @ws["on#{event}"] = => @trigger(event, arguments...)

    @state = Socket.CONNECTING

  onOpen: (event) ->
    if @connectedOnce
      @trigger('reconnect')
    else
      @trigger('connect')

    @connectedOnce = true
    @state = Socket.CONNECTED
    @flushBuffer()
    @resetRetries()

  _triggerFailures: ->
    if @state == Socket.CONNECTING
      @trigger 'connect_failed'

  onClose: (event) ->
    @trigger('disconnect', event?.reason ? '')

    if event?.code == DONT_COME_BACK_CODE
      @mustNeverReconnect = true

    @_triggerFailures()
    @_clearPingTimeout()
    @reconnect(event)

  onError: (event) ->
    @_triggerFailures()
    @disconnect()
    @reconnect()

  onMessage: (event) ->
    @_resetPingTimeout()
    if event.data == ''
      # This looks like a ping
      log 'socket', "responding to ping"
      @ws.send('')
      return

    try
      parsedData = JSON.parse(event.data)
    @trigger('data', parsedData)

  _shouldNotReconnect: ->
    if @reconnectNotAttemptedReason?
      @previousReason = @reconnectNotAttemptedReason

    @reconnectNotAttemptedReason = if @failedConsecutiveReconnectAttempts >= @failedConsecutiveReconnectsLimit
        'too many consecutive failures'
      else if @mustNeverReconnect
        'server NEVER RECONNECT code received'
      else if @reconnectRateAttempts >= @reconnectRateLimit
        'rate limited'
      else if @totalReconnects >= @maxReconnectsPerDay
        'max reconnects limit reached'

      @reconnectNotAttemptedReason?

  reconnect: (event) ->
    switch @state
      when Socket.CONNECTED
        @ws.close()
      when Socket.RECONNECTING, Socket.WAITING_TO_RECONNECT, Socket.DESTROYED
        return

    @_setReconnectBackOff()

    previousCallAttemptedReconnect = !@reconnectNotAttemptedReason?

    if @_shouldNotReconnect()
      if previousCallAttemptedReconnect
        @trigger('reconnect_failed')
      @state = Socket.CLOSED
      return

    @state = Socket.WAITING_TO_RECONNECT

    if @failedConsecutiveReconnectAttempts == 0
      @currentReconnectDelay = @fxReconnectDelay(event)

    log "socket", "attempting reconnect in #{@currentReconnectDelay}ms"

    @_reconnectTimer = setTimeout =>
      @_reconnectTimer = null
      @connect()
      @state = Socket.RECONNECTING
      @trigger('reconnecting')
    , @currentReconnectDelay

    @totalReconnects++
    @failedConsecutiveReconnectAttempts++
    @reconnectRateAttempts++

    @currentReconnectDelay = Math.min(2 * @currentReconnectDelay, @maxReconnectDelay)

  # If a reconnect fails *really* quickly, we assume it's due to not having a connection
  # In this case, we can adjust the token buckets back down as we're not taxing the backend
  # This gives people more chance to re-connect while their wifi is connecting.
  _adjustTokenBuckets: ->
    fastFailure = @connectAttemptStarted >= (Date.now() - 20)
    if @state == Socket.RECONNECTING && (!navigator.onLine || fastFailure)
      @totalReconnects = Math.max(@totalReconnects - 1, 0)
      @failedConsecutiveReconnectAttempts = Math.max(@failedConsecutiveReconnectAttempts - 1, 0)
      @reconnectRateAttempts = Math.max(@reconnectRateAttempts - 1, 0)
      @currentReconnectDelay = @currentReconnectDelay / 2

  disconnect: ->
    if @state == Socket.DESTROYED
      return

    @_clearPingTimeout()
    @_adjustTokenBuckets()

    state = @state
    # This needs to happen before @onClose, or we infinitely recurse
    @state = Socket.CLOSED

    switch state
      when Socket.CONNECTED, Socket.CONNECTING, Socket.RECONNECTING
        @ws.close()
        # A client-initiated close doesn't trigger the handler
        @onClose()
      when Socket.WAITING_TO_RECONNECT
        clearTimeout @_reconnectTimer

  send: (data) ->
    if @state == Socket.CONNECTED
      try
        @ws.send data
      catch
        @buffer.push data
        @onError()
    else
      @buffer.push data

  flushBuffer: ->
    buf = @buffer
    @buffer = []
    for data in buf
      @send(data)

  destroy: ->
    # Clean up all the related timers to allow this object to be GC'd
    clearTimeout @_reconnectTimer
    clearTimeout @_timeoutBackoff
    clearTimeout @_pingTimeout
    clearInterval @_dailyFailsCheck
    clearInterval @_wakeUpCheck

    @state = Socket.DESTROYED



// WEBPACK FOOTER //
// ./app/scripts/network/socket.coffee